import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { complaintsApi, complaintsQueryKeys } from '../api';
import type { ComplaintFilters, CreateComplaintDto, UpdateComplaintDto } from '../types';

export function useComplaints(filters?: ComplaintFilters) {
  return useQuery({
    queryKey: complaintsQueryKeys.list(filters),
    queryFn: () => complaintsApi.getAll(filters),
  });
}

export function useComplaint(id: number | string) {
  return useQuery({
    queryKey: complaintsQueryKeys.detail(id),
    queryFn: () => complaintsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateComplaint() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateComplaintDto) => complaintsApi.create(data),
    onSuccess: () => {
      toast.success(t('complaintCreatedSuccess'));
      queryClient.invalidateQueries({ queryKey: complaintsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingComplaint', { error: 'Unknown error' })));
    },
  });
}

export function useUpdateComplaint() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateComplaintDto }) =>
      complaintsApi.update({ id, data }),
    onSuccess: (_, variables) => {
      toast.success(t('complaintUpdatedSuccess'));
      queryClient.invalidateQueries({ queryKey: complaintsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: complaintsQueryKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingComplaint', { error: 'Unknown error' })));
    },
  });
}

export function useDeleteComplaint() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => complaintsApi.delete(id),
    onSuccess: () => {
      toast.success(t('complaintDeletedSuccess'));
      queryClient.invalidateQueries({ queryKey: complaintsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingComplaint', { error: 'Unknown error' })));
    },
  });
}
